import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Radio, Trash2, Plus, Info } from 'lucide-react';
import * as routingApi from '@/api/notification-routing.api';
import type { NumeroAviso, RolNumero, RoutingConfig } from '@/api/notification-routing.api';
import type { MessagesConfig } from '@/api/installation.api';
import { ApiError } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/hooks/useToast';
import { normalizeAvisoPhone } from '@conversia/shared-types';
import { validateManualClient } from '@/lib/manual-client';
import {
  MESSAGE_TEMPLATE_CATALOG,
  TEMPLATE_SECTIONS,
  type MessageTemplateMeta,
  type TemplateSeccion,
} from '@/lib/message-templates';
import { cn } from '@/lib/cn';

/**
 * Pestaña Mensajes REDISEÑADA (feature/rediseno-config): tarjetas compactas
 * por plantilla con SU switch integrado (los switches EXISTENTES del ruteo —
 * mismo Setting, solo cambia dónde se ven), agrupadas por sección, con chips
 * para insertar variables. Al final, el directorio de números y los destinos
 * de los avisos internos.
 *
 * Dos guardados, sin carreras: los TEXTOS viajan con el «Guardar cambios»
 * general de la página (config de instalación); los switches/números/destinos
 * viven en el Setting de ruteo y se guardan con su propio botón, que aparece
 * solo cuando hay cambios.
 */

const ROL_LABELS: Record<RolNumero, string> = {
  dueno: 'Dueño',
  secretaria: 'Secretaria',
  encargado_tecnicos: 'Encargado de técnicos',
  otro: 'Otro',
};

const ROL_OPTIONS = (Object.entries(ROL_LABELS) as [RolNumero, string][]).map(([value, label]) => ({
  value,
  label,
}));

const SECCIONES: TemplateSeccion[] = ['cliente', 'interno', 'tecnico'];

export function MensajesTab({
  messages,
  onChange,
}: {
  messages: MessagesConfig;
  onChange: (patch: Partial<MessagesConfig>) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['notification-routing'],
    queryFn: routingApi.getRouting,
  });

  const [routing, setRouting] = useState<RoutingConfig | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setRouting(data.config);
      setDirty(false);
    }
  }, [data]);

  const saveRouting = useMutation({
    mutationFn: (config: RoutingConfig) => routingApi.saveRouting(config),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-routing'] });
      toast.success('Avisos y ruteo guardados');
      setDirty(false);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar el ruteo'),
  });

  if (isLoading || !data || !routing) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const canales = data.canales;
  const canalEmisor = canales.find((c) => c.status === 'CONNECTED') ?? canales[0] ?? null;
  const emisorVivo = canalEmisor?.status === 'CONNECTED';

  function isOn(trigger: string): boolean {
    return routing!.rutas[trigger]?.enabled !== false;
  }

  function setRuta(trigger: string, patch: Partial<{ enabled: boolean; numeroIds: string[] }>) {
    setRouting((r) =>
      r
        ? {
            ...r,
            rutas: {
              ...r.rutas,
              [trigger]: {
                enabled: r.rutas[trigger]?.enabled ?? true,
                numeroIds: r.rutas[trigger]?.numeroIds ?? [],
                ...patch,
              },
            },
          }
        : r,
    );
    setDirty(true);
  }

  return (
    <div className="space-y-8">
      {/* Cabecera: leyenda de variables + estado del canal emisor. */}
      <div className="space-y-2">
        <p className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Las palabras entre {'{{llaves}}'} se rellenan solas con los datos reales de cada caso.
          Usa los chips «+ variable» de cada mensaje para insertarlas.
        </p>
        <p
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2 text-sm',
            emisorVivo
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
          )}
        >
          <Radio className="h-4 w-4 shrink-0" />
          {canalEmisor ? (
            <span>
              Todo sale por el canal <b>{canalEmisor.name}</b> —{' '}
              {emisorVivo ? 'CONECTADO' : 'CAÍDO (ahora mismo nada puede salir)'}
            </span>
          ) : (
            <span>No hay canal de WhatsApp: ningún mensaje puede salir.</span>
          )}
        </p>
      </div>

      {/* Secciones de tarjetas. */}
      {SECCIONES.map((seccion) => {
        const meta = TEMPLATE_SECTIONS[seccion];
        const tarjetas = MESSAGE_TEMPLATE_CATALOG.filter((t) => t.seccion === seccion);
        return (
          <section key={seccion} className="space-y-3">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                {meta.titulo}
              </h3>
              <p className="mt-0.5 text-xs text-slate-400">{meta.hint}</p>
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
              {tarjetas.map((t) => (
                <TemplateCard
                  key={t.key}
                  meta={t}
                  value={messages[t.key]}
                  enabled={isOn(t.trigger)}
                  onToggle={(next) => setRuta(t.trigger, { enabled: next })}
                  onText={(text) => onChange({ [t.key]: text } as Partial<MessagesConfig>)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* Destinos de los avisos internos + directorio de números. */}
      <DestinosCard
        routing={routing}
        canalesPhones={new Set(canales.map((c) => c.phone).filter(Boolean))}
        isOn={isOn}
        setRuta={setRuta}
        onAddNumero={(n) => {
          setRouting((r) => (r ? { ...r, numeros: [...r.numeros, n] } : r));
          setDirty(true);
        }}
        onRemoveNumero={(id) => {
          setRouting((r) =>
            r
              ? {
                  ...r,
                  numeros: r.numeros.filter((n) => n.id !== id),
                  rutas: Object.fromEntries(
                    Object.entries(r.rutas).map(([k, ruta]) => [
                      k,
                      { ...ruta, numeroIds: ruta.numeroIds.filter((nid) => nid !== id) },
                    ]),
                  ),
                }
              : r,
          );
          setDirty(true);
        }}
      />

      {/* Guardado del ruteo: aparece SOLO con cambios pendientes. */}
      {dirty && (
        <div className="sticky bottom-4 z-10 flex justify-end">
          <div className="flex items-center gap-3 rounded-full bg-white px-4 py-2 shadow-lg ring-1 ring-surface-border dark:bg-slate-900 dark:ring-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Cambios de switches y números sin guardar
            </span>
            <Button size="sm" onClick={() => saveRouting.mutate(routing)} loading={saveRouting.isPending}>
              Guardar avisos y ruteo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Tarjeta de UNA plantilla: título + switch propio + editor + chips. */
function TemplateCard({
  meta,
  value,
  enabled,
  onToggle,
  onText,
}: {
  meta: MessageTemplateMeta;
  value: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  onText: (text: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  /** Inserta el token en la posición del cursor (o al final). */
  function insert(token: string) {
    const el = ref.current;
    if (!el) {
      onText(`${value}${token}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    onText(next);
    // Devuelve el foco tras el re-render, con el cursor después del token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  return (
    <Card className={cn('transition-opacity', !enabled && 'opacity-60')}>
      <CardBody className="space-y-2.5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{meta.titulo}</p>
            <p className="mt-0.5 text-xs leading-snug text-slate-400">{meta.cuando}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
            <span className={cn('text-[11px] font-medium', enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400')}>
              {enabled ? 'Activo' : 'Apagado'}
            </span>
            <Toggle checked={enabled} onChange={onToggle} label={`${meta.titulo}: encendido o apagado`} />
          </div>
        </div>
        <Textarea
          ref={ref}
          aria-label={`Texto: ${meta.titulo}`}
          rows={meta.rows}
          value={value}
          onChange={(e) => onText(e.target.value)}
          className="text-sm"
        />
        {meta.variables.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[11px] text-slate-400">+ variable:</span>
            {meta.variables.map((v) => (
              <button
                key={v.token}
                type="button"
                title={`Inserta ${v.token}`}
                onClick={() => insert(v.token)}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Destinos de los avisos internos: directorio de números con rol + a quién va
 * cada aviso (N:N) + los 2 avisos del sistema sin plantilla (reporte diario y
 * alertas del guardián) con su switch aquí.
 */
function DestinosCard({
  routing,
  canalesPhones,
  isOn,
  setRuta,
  onAddNumero,
  onRemoveNumero,
}: {
  routing: RoutingConfig;
  canalesPhones: Set<string | null>;
  isOn: (trigger: string) => boolean;
  setRuta: (trigger: string, patch: Partial<{ enabled: boolean; numeroIds: string[] }>) => void;
  onAddNumero: (n: NumeroAviso) => void;
  onRemoveNumero: (id: string) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [phone, setPhone] = useState('');
  const [rol, setRol] = useState<RolNumero>('otro');
  const [error, setError] = useState<string | null>(null);

  // Avisos internos ruteables: los que tienen plantilla (título del catálogo)
  // + los 2 del sistema sin plantilla, con switch propio aquí.
  const internosConPlantilla = MESSAGE_TEMPLATE_CATALOG.filter((t) => t.seccion === 'interno');
  const sinPlantilla = [
    { trigger: 'reporte_diario', titulo: 'Reporte diario 8AM' },
    { trigger: 'alerta_guardian', titulo: 'Alertas del guardián (canal caído, backup, disco, cupo)' },
  ];

  function agregar() {
    const v = validateManualClient({ nombre, telefono: phone });
    if (!v.ok) {
      setError(v.error);
      return;
    }
    // El chip guarda el número YA normalizado (con código de país): así el
    // badge "número del canal" compara manzanas con manzanas.
    const normalized = normalizeAvisoPhone(phone);
    if (!normalized.ok) {
      setError(normalized.error);
      return;
    }
    setError(null);
    onAddNumero({
      id: `n${Date.now().toString(36)}`,
      nombre: nombre.trim(),
      rol,
      phone: normalized.phone,
    });
    setNombre('');
    setPhone('');
    setRol('otro');
  }

  function toggleDestino(trigger: string, numeroId: string) {
    const actual = routing.rutas[trigger]?.numeroIds ?? [];
    setRuta(trigger, {
      numeroIds: actual.includes(numeroId)
        ? actual.filter((id) => id !== numeroId)
        : [...actual, numeroId],
    });
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
          ¿A quién le llegan los avisos internos?
        </h3>
        <p className="mt-0.5 text-xs text-slate-400">
          Marca los números de cada aviso. Un aviso sin números marcados va al número del dueño.
        </p>
      </div>
      <Card>
        <CardBody className="space-y-5 p-4">
          {/* Directorio compacto. */}
          <div className="space-y-2">
            {routing.numeros.map((n) => (
              <div
                key={n.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-surface-border px-3 py-1.5 text-sm dark:border-slate-700"
              >
                <span className="font-medium text-slate-900 dark:text-slate-100">{n.nombre}</span>
                <Badge variant="info">{ROL_LABELS[n.rol]}</Badge>
                <span className="text-slate-500">{n.phone}</span>
                {canalesPhones.has(n.phone) && <Badge variant="success">número del canal</Badge>}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Quitar ${n.nombre}`}
                  className="ml-auto h-7 w-7 text-slate-400 hover:text-red-600"
                  onClick={() => onRemoveNumero(n.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap items-end gap-2">
              <Input label="Nombre" placeholder="Secretaria" className="w-36" value={nombre} onChange={(e) => setNombre(e.target.value)} />
              <Input label="Teléfono" placeholder="18295550000" className="w-36" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Select label="Rol" className="w-44" options={ROL_OPTIONS} value={rol} onChange={(e) => setRol(e.target.value as RolNumero)} />
              <Button variant="outline" size="sm" className="mb-0.5" onClick={agregar}>
                <Plus className="h-4 w-4" /> Agregar
              </Button>
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
          </div>

          {/* Destinos por aviso. */}
          <div className="divide-y divide-surface-border rounded-md border border-surface-border dark:divide-slate-700 dark:border-slate-700">
            {internosConPlantilla.map((t) => (
              <DestinoRow
                key={t.trigger}
                titulo={t.titulo}
                enabled={isOn(t.trigger)}
                numeros={routing.numeros}
                seleccion={routing.rutas[t.trigger]?.numeroIds ?? []}
                onToggleDestino={(id) => toggleDestino(t.trigger, id)}
              />
            ))}
            {sinPlantilla.map((t) => (
              <DestinoRow
                key={t.trigger}
                titulo={t.titulo}
                enabled={isOn(t.trigger)}
                numeros={routing.numeros}
                seleccion={routing.rutas[t.trigger]?.numeroIds ?? []}
                onToggleDestino={(id) => toggleDestino(t.trigger, id)}
                toggle={{
                  value: isOn(t.trigger),
                  onChange: (next) => setRuta(t.trigger, { enabled: next }),
                }}
              />
            ))}
          </div>
        </CardBody>
      </Card>
    </section>
  );
}

/** Fila de un aviso interno: título (+ toggle si no tiene tarjeta) + chips de destino. */
function DestinoRow({
  titulo,
  enabled,
  numeros,
  seleccion,
  onToggleDestino,
  toggle,
}: {
  titulo: string;
  enabled: boolean;
  numeros: NumeroAviso[];
  seleccion: string[];
  onToggleDestino: (numeroId: string) => void;
  /** Solo los avisos sin tarjeta traen su switch aquí. */
  toggle?: { value: boolean; onChange: (next: boolean) => void };
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2">
      <span className={cn('text-sm text-slate-700 dark:text-slate-200', !enabled && 'text-slate-400 line-through')}>
        {titulo}
      </span>
      {toggle && (
        <Toggle checked={toggle.value} onChange={toggle.onChange} label={`${titulo}: encendido o apagado`} className="ml-1" />
      )}
      <span className="ml-auto flex flex-wrap gap-1">
        {numeros.map((n) => (
          <button
            key={n.id}
            type="button"
            disabled={!enabled}
            onClick={() => onToggleDestino(n.id)}
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-40',
              seleccion.includes(n.id)
                ? 'bg-acento-lleno text-acento-sobre'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            {n.nombre}
          </button>
        ))}
        {numeros.length === 0 ? (
          <span className="text-xs text-slate-400">→ dueño</span>
        ) : (
          seleccion.length === 0 && <span className="text-xs text-slate-400">→ dueño</span>
        )}
      </span>
    </div>
  );
}
